import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { ErrorReportRequest, ErrorSourceType } from '../interfaces/error-report.model';
import { AppConstants } from '../commons/app-constants';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class ErrorReportService {
  // HttpBackend bypasses all interceptors — prevents circular dependency
  // and ensures error reporting never triggers another interceptor error cycle.
  private http: HttpClient;

  private readonly endpoint = `${AppConstants.API_URL}api/v1/errors/report`;

  // Deduplication: suppress identical messages within 30 seconds
  private readonly recentErrors = new Map<string, number>();
  private readonly DEDUPE_WINDOW_MS = 30_000;

  constructor(backend: HttpBackend) {
    this.http = new HttpClient(backend);
  }

  report(
    errorType: ErrorSourceType,
    message: string,
    options: {
      stackTrace?: string;
      statusCode?: number;
      requestUrl?: string;
    } = {}
  ): void {
    if (!message) return;

    const dedupeKey = `${errorType}:${message}:${options.statusCode ?? ''}`;
    if (this.isDuplicate(dedupeKey)) return;

    const payload: ErrorReportRequest = {
      errorType,
      message: message.slice(0, 1000),
      stackTrace: options.stackTrace?.slice(0, 4000),
      url: window.location.href,
      userAgent: navigator.userAgent,
      statusCode: options.statusCode,
      requestUrl: options.requestUrl,
      timestamp: new Date().toISOString(),
    };

    this.http.post(this.endpoint, payload, { withCredentials: true })
      .subscribe({ error: () => {} }); // silent fail — never let error reporting crash the app

    // Forward HTTP errors to Sentry (no-op if Sentry not initialized)
    Sentry.withScope(scope => {
      scope.setTag('errorType', errorType);
      if (options.statusCode) scope.setTag('statusCode', String(options.statusCode));
      if (options.requestUrl) scope.setTag('requestUrl', options.requestUrl);
      Sentry.captureMessage(message, 'error');
    });
  }

  private isDuplicate(key: string): boolean {
    const now = Date.now();
    const last = this.recentErrors.get(key);
    if (last && now - last < this.DEDUPE_WINDOW_MS) return true;
    this.recentErrors.set(key, now);
    // Prevent unbounded map growth
    if (this.recentErrors.size > 100) {
      const oldest = this.recentErrors.keys().next().value;
      if (oldest) this.recentErrors.delete(oldest);
    }
    return false;
  }
}
